export const OUTCOMES = [
  "unchanged",
  "proposed",
  "unsupported",
  "unmapped",
  "refused"
] as const;

export type Outcome = (typeof OUTCOMES)[number];

export interface BoxGeometry {
  readonly kind: "box";
  readonly position: readonly [number, number, number];
  readonly size: readonly [number, number, number];
  readonly rotationY?: number;
}

/**
 * A tessellated triangle mesh decoded from `ifc-mesh-wasm` (see
 * src/domain/ifc-mesh-import.ts and src/vendor/ifc-mesh-wasm-0.4.1/NOTICE.md). Vertex positions are
 * already absolute world-space metres (the engine's own Z-up convention, matching IFC itself) --
 * unlike `BoxGeometry.position`, nothing further translates or rotates this at render time.
 */
export interface MeshGeometry {
  readonly kind: "mesh";
  readonly vertexPositions: Float32Array;
  readonly triangleIndices: Uint32Array;
  readonly triangleCount: number;
}

export type ElementGeometry = BoxGeometry | MeshGeometry;

export interface Classification {
  readonly system: string;
  readonly code: string;
  readonly label: string;
}

export interface FixtureElement {
  readonly id: string;
  readonly label: string;
  readonly type: string;
  readonly properties: Readonly<Record<string, unknown>>;
  readonly quantities: Readonly<Record<string, number>>;
  readonly classification: Classification | null;
  readonly geometry: ElementGeometry | null;
  readonly editable: boolean;
  readonly unsupportedReason?: string;
  readonly unknown: Readonly<Record<string, unknown>>;
}

export interface AnonymousFixture {
  readonly id: string;
  readonly label: string;
  readonly elements: readonly FixtureElement[];
}

export interface MappingRequest {
  readonly property: string;
  readonly value: string;
}

export interface ProposalEntry {
  readonly elementId: string;
  readonly outcome: Outcome;
  readonly reason?: string;
  readonly before: Readonly<Record<string, unknown>>;
  readonly after: Readonly<Record<string, unknown>>;
  readonly diff: readonly PropertyDiff[];
  readonly unknown: Readonly<Record<string, unknown>>;
}

export interface PropertyDiff {
  readonly path: string;
  readonly before: unknown;
  readonly after: unknown;
}

export interface CanonicalProposal {
  readonly schemaVersion: "d0-proposal-v1";
  readonly fixtureId: string;
  readonly request: MappingRequest;
  readonly entries: readonly ProposalEntry[];
  readonly validation: {
    readonly state: "partial";
    readonly reason: string;
    readonly assessedElementCount: number;
  };
}
